import { RefObject, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

export enum DnDItemType {
  ADMINISTRATION_LIST_ELEMENT = "administration-list-element",
  MULTIPLE_CHOICE_QUESTION = "multiple-choice-question",
  MULTIPLE_CHOICE_QUESTION_OPTION = "multiple-choice-question-option",
}

interface DragItem {
  index: number;
  type: string;
  id: string;
}

export function useDragAndDropElement(
  id: string,
  index: number,
  accept: DnDItemType,
  moveElement: (dragIndex: number, hoverIndex: number) => void,
  saveOrder: (dragIndex: number, dropIndex: number, id: string) => void,
): [RefObject<HTMLLIElement>, boolean] {
  const ref = useRef<HTMLLIElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: accept,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept,
    hover(dragItem, monitor) {
      if (!ref.current) {
        return;
      }

      const item = dragItem as DragItem;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientY = (clientOffset?.y ?? 0) - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveElement(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
    drop(dragItem) {
      const item = dragItem as DragItem;
      saveOrder(item.index, index, item.id);
    },
  });

  //eslint-disable-next-line
  drag(drop(ref));

  return [ref, isDragging];
}
